import React from 'react';
import { arrayOf, bool, func, node, number, object, oneOfType, string } from 'prop-types';
import { Box, FormControl, FormHelperText, InputLabel, OutlinedInput, styled, Typography } from '@mui/material';

const InputWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'fullWidth',
})(({ fullWidth }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  width: fullWidth ? '100%' : 'max-content',

  '.show-on-hover': {
    display: 'none',
  },
  '&:hover .show-on-hover': {
    display: 'block',
  },

  '.hide-on-hover': {
    display: 'block',
  },
  '&:hover .hide-on-hover': {
    display: 'none',
  },
}));

/**
 * @param {node} actions - optional node for any input actions,
 * such as copy, delete, regenerate buttons, etc.
 * @param {boolean} showActionsOnHover - if true, the actions will
 * only be shown when the user hovers over the input field
 * @param {boolean} fullWidth - if true, the input field will take
 * the full width of its container, otherwise defaults to 360px
 */
const InputField = (props) => {
  const {
    id,
    inputName,
    inputValues,
    inputType,
    label,
    description,
    inputPlaceholder,
    inputDisabled,
    inputReadOnly,
    handleChange,
    handleFocus,
    handleBlur,
    required,
    errorMessage,
    autoFocus,
    minlength,
    maxlength,
    InputProps,
    actions,
    showActionsOnHover,
    fullWidth,
    customInputWidth,
    startAdornment,
    endAdornment,
    sizeLarge,
    rows,
    error,
    chip,
  } = props;

  const inputElementProps = {
    ...InputProps,
    ...(inputType === 'password' ? { minLength: 8, maxLength: 64 } : { minLength: minlength, maxLength: maxlength }),
  };

  return (
    <InputWrapper fullWidth={fullWidth}>
      <FormControl
        variant="outlined"
        disabled={inputDisabled}
        error={error === undefined ? errorMessage.length > 0 : error}
        fullWidth={fullWidth}
        sx={{ width: fullWidth ? '100%' : (customInputWidth || '360px') }}
      >
        {label && (
          <InputLabel htmlFor={id || inputName} required={required} shrink>
            {label}
          </InputLabel>
        )}
        {description && (
          <Typography variant="caption" sx={{ marginBottom: 1 }}>{description}</Typography>
        )}
        <OutlinedInput
          id={id || inputName}
          name={inputName}
          type={inputType}
          value={inputValues || ''}
          placeholder={inputPlaceholder}
          disabled={inputDisabled}
          readOnly={inputReadOnly}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required={required}
          inputProps={inputElementProps}
          autoComplete="off"
          autoFocus={autoFocus}
          error={error === undefined ? errorMessage.length > 0 : error}
          fullWidth
          sx={theme => ({
            ...(sizeLarge && {
              fontSize: theme.typography.body1.fontSize,
            }),
          })}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          multiline={rows > 1}
          rows={rows}
        />
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
      {chip && <Box marginLeft={1} alignSelf="center" className={showActionsOnHover ? 'hide-on-hover' : ''}>{chip}</Box>}
      {actions && <Box marginLeft={1} className={showActionsOnHover ? 'show-on-hover' : ''}>{actions}</Box>}
    </InputWrapper>
  );
};

export default InputField;

InputField.propTypes = {
  id: string,
  label: string,
  description: string,
  autoFocus: bool,
  handleChange: func.isRequired,
  handleFocus: func,
  handleBlur: func,
  inputName: string,
  inputType: string,
  inputValues: oneOfType([string, object, arrayOf(string)]),
  inputPlaceholder: string,
  inputDisabled: bool,
  inputReadOnly: bool,
  errorMessage: string,
  required: bool,
  minlength: number,
  maxlength: number,
  // eslint-disable-next-line react/forbid-prop-types
  InputProps: object,
  actions: node,
  showActionsOnHover: bool,
  fullWidth: bool,
  customInputWidth: string,
  sizeLarge: bool,
  startAdornment: node,
  endAdornment: node,
  rows: number,
  error: bool,
  chip: node,
};

InputField.defaultProps = {
  id: '',
  label: '',
  description: '',
  handleFocus: null,
  handleBlur: null,
  autoFocus: false,
  inputName: '',
  inputType: 'text',
  inputValues: '',
  inputPlaceholder: '',
  inputDisabled: false,
  inputReadOnly: false,
  errorMessage: '',
  required: false,
  minlength: 2,
  maxlength: 255,
  InputProps: {},
  actions: null,
  showActionsOnHover: false,
  fullWidth: false,
  customInputWidth: '',
  sizeLarge: false,
  startAdornment: null,
  endAdornment: null,
  rows: 1,
  error: undefined,
  chip: null,
};
